import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { CONFIRMED, PENDING } from '../../../../utils/enumerator/enum-billing-payment-state';
import { ModalPaymentContainer } from './modal-payment-container';
import { getPrice } from '../../../../utils/helper/formatting';
import { submitCardPayment, PAYMENT_CARD_SUCCESS } from '../../../../store/actions/loan-actions';

function ModalCreditCardOptions(props) {
  const { show, onClose } = props;

  const [cardNumber, setCardNumber] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardExpirationDate, setCardExpirationDate] = useState('');
  const [cardSecurityCode, setCardSecurityCode] = useState('');
  const [cardPaymentType, setCardPaymentType] = useState('');
  const [cardInstallments, setCardInstallments] = useState('1');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  const amount = props.installments.reduce(
    (currentValue, { owedAmount }) => (currentValue || 0) + (owedAmount || 0),
    0,
  );

  const handleCardNumber = (event) => {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 16) {
      value = value.substring(0, 16);
    }
    setCardNumber(value);
  };

  const formatCardNumber = () => cardNumber.replace(/[^0-9]/g, '').replace(/(\d{4})(?=(\d)+(?!\d))/g, '$1 ');

  const handleCardHolderName = (event) => {
    setCardHolderName(event.target.value);
  };

  const handleCardExpirationDate = (event) => {
    // Format date.
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 4) {
      value = value.substring(0, 4);
    }
    value = value.replace(/(\d{2})(\d{1,2})$/g, '$1-$2');
    setCardExpirationDate(value);
  };

  const formatCardExpirationDate = () => cardExpirationDate.replace(/-/g, '/');

  const handleCardSecurityCode = (event) => {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 3) {
      value = value.substring(0, 3);
    }
    setCardSecurityCode(value);
  };

  const validateCardNumber = () =>
    // Validate card number.
    !!cardNumber && cardNumber.length === 16;
  const validateCardHolderName = () =>
    // Validate name.
    !!cardHolderName;
  const validateCardExpirationDate = () => {
    // Validate expiration date.
    const datePattern = /^(0[1-9]|1[0-2])-\d{2}$/;
    return !!cardExpirationDate && datePattern.test(cardExpirationDate);
  };

  const validateCardSecurityCode = () =>
    // Validate security code.
    !!cardSecurityCode && cardSecurityCode.length === 3;
  const validateCardPaymentType = () =>
    // Validate payment type.
    !!cardPaymentType;
  const validateCardInfo = () =>
    validateCardNumber() &&
    validateCardHolderName() &&
    validateCardExpirationDate() &&
    validateCardSecurityCode() &&
    validateCardPaymentType();

  const handlePayment = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    if (validateCardInfo()) {
      setProcessing(true);

      const creditCard = {
        number: cardNumber,
        holder: cardHolderName,
        expirationDate: cardExpirationDate.replace(/(\d{2})-(\d{2})/g, '20$2-$1-01'),
        securityCode: cardSecurityCode,
      };

      props
        .submitCardPayment(creditCard, amount, cardInstallments, props.device, cardPaymentType, props.loan.id)
        .then((response) => {
          // If request was successful.
          if (response.type === PAYMENT_CARD_SUCCESS) {
            // Get the bill.
            const bill = response.payload;
            // If payment is successful.
            if (bill?.payments?.[0].state === CONFIRMED) {
              setSuccessMessage('Pagamento realizado com sucesso!');
              setCardPaymentType('');
              setCardHolderName('');
              setCardNumber('');
              setCardExpirationDate('');
              setCardSecurityCode('');
            }
            // If payment is pending.
            else if (bill?.payments?.[0]?.state === PENDING) {
              window.location.href = bill.execution.additionalInformation.authenticationUrl;
            }
            // If payment was declined.
            else {
              setErrorMessage('O pagamento não foi autorizado.');
            }

            setProcessing(false);
          }
          // If request failed.
          else {
            const errorCode = response?.payload?.response?.[0]?.code;
            const statusCode = response?.payload?.status;
            if (['loan.bill.card.name.invalid'].includes(errorCode) || statusCode === 400) {
              setErrorMessage('Não é possível utilizar esse cartão.');
            }
            // Error has status code.
            else if (statusCode) {
              setErrorMessage('Um erro inesperado ocorreu. Por favor, tente novamente em alguns minutos.');
            }
            // Error does not have status code.
            else {
              setErrorMessage(
                'Um erro inesperado ocorreu. Por favor, tente novamente em alguns minutos ou entre em contato com o atendimento ao cliente.',
              );
            }

            setProcessing(false);
          }
        })
        // On unknown error.
        .catch((error) => {
          setErrorMessage('Um erro inesperado ocorreu. Por favor, entre em contato com o atendimento ao cliente.');
          setProcessing(false);
        });
    } else {
      setErrorMessage('Por favor, verifique se os dados do cartão estão preenchidos corretamente.');
    }
  };

  return (
    <>
      {show && (
        <ModalPaymentContainer
          headerTitle="Pagar com cartão"
          headerSubtitle={`Valor: ${getPrice(amount)}`}
          buttonText="Pagar"
          onClose={onClose}
          onButtonClick={handlePayment}
          icon={<FontAwesomeIcon icon={faCreditCard} size="4x" />}
          loadingButton={processing}
        >
          <div className="modal-credit-card-options">
            {/* Card payment method */}
            <div className="modal-credit-card-input-group">
              <div className="modal-credit-card-input-label">
                <label>Forma de pagamento</label>
              </div>
              <div className={`modal-credit-card-input${cardPaymentType ? ' valid' : ''}`}>
                <select value={cardPaymentType} onChange={(e) => setCardPaymentType(e.target.value)}>
                  <option value="" disabled>
                    ----
                  </option>
                  <option value="CREDIT_CARD">Crédito</option>
                  <option value="DEBIT_CARD">Débito</option>
                </select>
              </div>
            </div>
            {/* Card number */}
            <div className="modal-credit-card-input-group">
              <div className="modal-credit-card-input-label">
                <label>Número do cartão</label>
              </div>
              <div
                className={`modal-credit-card-input${cardNumber ? (validateCardNumber() ? ' valid' : ' invalid') : ''}`}
              >
                <input
                  type="text"
                  placeholder="0000 0000 0000 0000"
                  value={formatCardNumber()}
                  onChange={handleCardNumber}
                />
              </div>
            </div>
            {/* Card name */}
            <div className="modal-credit-card-input-group">
              <div className="modal-credit-card-input-label">
                <label>Nome do portador</label>
              </div>
              <div
                className={`modal-credit-card-input${
                  cardHolderName ? (validateCardHolderName() ? ' valid' : ' invalid') : ''
                }`}
              >
                <input type="text" placeholder="Vagner Silva" onChange={handleCardHolderName} value={cardHolderName} />
              </div>
            </div>
            <div className="modal-credit-card-input-row">
              {/* Due date */}
              <div className="modal-credit-card-input-group">
                <div className="modal-credit-card-input-label">
                  <label>Data de validade</label>
                </div>
                <div
                  className={`modal-credit-card-input${
                    cardExpirationDate ? (validateCardExpirationDate() ? ' valid' : ' invalid') : ''
                  }`}
                >
                  <input
                    type="text"
                    placeholder="02/21"
                    value={formatCardExpirationDate()}
                    onChange={handleCardExpirationDate}
                  />
                </div>
              </div>
              {/* Security code */}
              <div className="modal-credit-card-input-group">
                <div className="modal-credit-card-input-label">
                  <label>Cód. Segurança</label>
                </div>
                <div
                  className={`modal-credit-card-input${
                    cardSecurityCode ? (validateCardSecurityCode() ? ' valid' : ' invalid') : ''
                  }`}
                >
                  <input type="text" placeholder="123" value={cardSecurityCode} onChange={handleCardSecurityCode} />
                </div>
              </div>
            </div>
            {cardPaymentType === 'CREDIT_CARD' && (
              <div className="modal-credit-card-input-group">
                <div className="modal-credit-card-input-label">
                  <label>Parcelamento</label>
                </div>
                <div className="modal-credit-card-input">
                  <select value={cardInstallments} onChange={(e) => setCardInstallments(e.target.value)}>
                    <option value="1">Parcelado em 1 vez</option>
                    <option value="2">Parcelado em 2 vezes</option>
                    <option value="3">Parcelado em 3 vezes</option>
                    <option value="4">Parcelado em 4 vezes</option>
                    <option value="5">Parcelado em 5 vezes</option>
                    <option value="6">Parcelado em 6 vezes</option>
                  </select>
                </div>
              </div>
            )}

            {successMessage && <div className="modal-credit-card-success-message">{successMessage}</div>}
            {errorMessage && <div className="modal-credit-card-error-message">{errorMessage}</div>}
          </div>
        </ModalPaymentContainer>
      )}
    </>
  );
}

ModalCreditCardOptions.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  installments: PropTypes.arrayOf(PropTypes.object).isRequired,
};

ModalCreditCardOptions.defaultProps = {
  show: false,
};

const mapStateToProps = (state) => ({
  device: state.customer.device,
  loan: state.personalLending.loan,
});

const mapDispatchToProps = (dispatch) => ({
  submitCardPayment: (creditCard, amount, installmentsCard, device, paymentType, loanId) =>
    dispatch(submitCardPayment(creditCard, amount, installmentsCard, device, paymentType, loanId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModalCreditCardOptions);
